// src/components/organization/categories/CreateCategoryModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import CategoryFormFields from "./CategoryFormFields";
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";

export default function CreateCategoryModal({
  isOpen,
  onClose,
  token,
  defaultOrganizationId,
  allCategories = [],
  onCreated, // callback بعد از ساخت موفق
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const categoryScopes = Array.isArray(user?.categoryScopes)
    ? user.categoryScopes.map(String)
    : [];

  async function createCategory(payload) {
    return httpRequest("POST", "/category", payload, {
      "x-access-token": user.token,
    });
  }

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "organization",
    organization: defaultOrganizationId || "",
    parent: "",
    slug: "",
    icon: "",
    hasSubscription: false,
  });
  const [creating, setCreating] = useState(false);

  // فقط خودِ scopeها به‌عنوان «والد مجاز»
  const parentCandidates = useMemo(() => {
    const scopeSet = new Set(categoryScopes);
    const list = (allCategories || []).filter((c) =>
      scopeSet.has(String(c._id))
    );
    // اگر والد لول 3 باشد، فرزند لول 4 می‌شود که مجاز نیست → بعداً در submit جلویش را می‌گیریم
    return list;
  }, [allCategories, categoryScopes]);

  // اگر فقط یک والدِ مجاز داریم، به‌صورت خودکار انتخابش کن
  useEffect(() => {
    if (!isOpen) return;
    if (parentCandidates.length === 1) {
      setForm((f) => ({ ...f, parent: String(parentCandidates[0]._id) }));
    }
  }, [isOpen, parentCandidates]);

  const selectedParent = useMemo(
    () => allCategories.find((c) => c._id === form.parent),
    [allCategories, form.parent]
  );
  const willBeLevel = selectedParent ? (selectedParent.level || 1) + 1 : 1;
  const canToggleHasSubscription = willBeLevel === 3;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!isOpen) return;

    const hasParent = !!form.parent;

    // ✅ اگر کاربر اسکوپ دارد، «فقط وقتی parent انتخاب شده» باید از بین parentCandidates باشد
    if (categoryScopes.length > 0 && hasParent) {
      const ok = parentCandidates.some((c) => String(c._id) === form.parent);
      if (!ok) {
        toast.error(
          "انتخاب والد نامعتبر است. فقط از بین دسته‌های مجاز انتخاب کنید."
        );
        return;
      }
      // ✅ جلوگیری از ساخت لول ۴ (زیرمجموعه سطح ۳)
      const p = parentCandidates.find((c) => String(c._id) === form.parent);
      if (p && Number(p.level) >= 3) {
        toast.error("امکان ساخت زیرمجموعه برای سطح ۳ وجود ندارد.");
        return;
      }
    } else if (!categoryScopes.length && selectedParent) {
      // حالت بدون اسکوپ: اگر والد انتخاب شده و لول 3 است، باز هم ممنوع
      if (Number(selectedParent.level) >= 3) {
        toast.error("امکان ساخت زیرمجموعه برای سطح ۳ وجود ندارد.");
        return;
      }
    }
    // ⚠️ نکته مهم:
    // اگر categoryScopes.length > 0 و parent خالی باشد،
    // اینجا هیچ محدودیتی اعمال نمی‌کنیم ⇒ یعنی مجاز به ساخت دسته‌بندی سطح ۱ هست.

    setCreating(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || undefined,
        type: form.type,
        organization:
          form.type === "organization"
            ? form.organization || defaultOrganizationId
            : undefined,
        parent: form.parent || undefined,
        slug: form.slug || undefined,
        icon: form.icon || undefined,
        hasSubscription: canToggleHasSubscription
          ? form.hasSubscription
          : false,
      };

      const res = await createCategory(payload);
      if (res?.data?.data?._id) {
        onCreated?.();
        // ریست فرم
        setForm({
          title: "",
          description: "",
          type: "organization",
          organization: defaultOrganizationId || "",
          parent: "",
          slug: "",
          icon: "",
          hasSubscription: false,
        });
        onClose?.();
      } else {
        toast.error(res?.message || t("common.unknown_error"));
      }
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  // ⚙️ هشدار فقط وقتی معنی دارد که:
  // - کاربر اسکوپ دارد
  // - هیچ والد مجازی وجود ندارد
  // - و کاربر «والدی انتخاب کرده باشد» (برای root دیگر مهم نیست)
  const showParentWarning =
    categoryScopes.length > 0 &&
    parentCandidates.length === 0 &&
    !!form.parent;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl p-4 md:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("org_categories.create_category")}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        {/* هشداری کوچک اگر کاربر اسکوپ دارد اما هیچ والد مجازی ندارد و سعی کرده والد بزند */}
        {showParentWarning && (
          <div className="mb-3 p-2 rounded-md border text-amber-700 border-amber-200 bg-amber-50 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>
              شما والدِ مجازی برای ساخت زیر‌دسته ندارید. می‌توانید والد را
              خالی بگذارید تا یک دسته‌بندی سطح ۱ بسازید.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <CategoryFormFields
            form={form}
            setForm={setForm}
            allCategories={allCategories}
            parentCandidates={parentCandidates}
            willBeLevel={willBeLevel}
            canToggleHasSubscription={canToggleHasSubscription}
            isOrgContext={true}
          />

          <div
            className={`flex ${
              isRtl ? "justify-start" : "justify-end"
            } gap-3 pt-2`}
          >
            <button
              type="button"
              className="px-4 py-2 rounded-lg border hover:bg-gray-50"
              onClick={onClose}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              // ❗ دیگر با showParentWarning کلی غیرفعال نمی‌کنیم؛
              // فقط اگر واقعاً تلاش برای زیر‌دسته‌ی غیرمجاز باشد، در handleSubmit جلوش گرفته می‌شود.
              disabled={creating}
              className={`px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2`}
            >
              <Plus className="w-4 h-4" />
              {creating
                ? t("common.saving")
                : t("org_categories.create_category")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
